import { useState, useCallback, useRef } from "react";
import { parse } from "date-fns";
import { toast } from "react-toastify";
import { fetchStepsData } from "api/weeklyTracker";
import { connectWithGoogle } from "api/authService";
import { googleLogout } from "@api/progressTracker";

export const useDeviceChart = () => {
  const stored = localStorage.getItem("is_google_fit_connect");
  const initialValue = stored === "true";

  const [googleFitConnect, setGoogleFitConnect] = useState(initialValue);
  const [datesAvailable, setDatesAvailable] = useState([]);
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [connectingGoogleFit, setConnectingGoogleFit] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [logoutLoading, setLogoutLoading] = useState(false);

  // Week navigation states
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [weeklyData, setWeeklyData] = useState([]);
  const [overallData, setOverallData] = useState(null);

  // Calendar states
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Current week date range
  const [currentWeekRange, setCurrentWeekRange] = useState({
    startDate: null,
    endDate: null,
  });

  // Data states
  const [chartData, setChartData] = useState({
    steps: [],
    calories: [],
    activeMinutes: [],
  });

  const [goals, setGoals] = useState({
    step_goal: 0,
    goal_calories: 0,
    goal_active_minutes: 0,
  });

  const [summaryStats, setSummaryStats] = useState({
    totalSteps: "0",
    caloriesBurnt: "0 kcal",
    activeTime: "0m",
    distance: "0 km",
    progressPercentage: "0",
    weeklyGoalProgress: "0",
    totalDistance: "0 m",
    averageSteps: "0",
    averageCalories: "0 kcal",
    averageActiveTime: "0m",
    fat_free_mass_kg: "0 kg",
    sleep_minutes: "0 m",
    weight: "0 kg",
    muscle_mass_kg: "0 kg",
    body_fat_percentage: "0 %",
    bmi: "0",
    blood_pressure: "0 mmHg",
    heart_rate: "0 bpm",
  });

  // Add ref to track if data has been fetched after connection
  const hasFetchedAfterConnect = useRef(false);
  // Add ref to track retry attempts
  const retryCountRef = useRef(0);

  const getSystemTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Main data fetching function
  const handleFetchData = useCallback(
    async (isSilent = false) => {
      if (dataLoading) return;

      // Only show global loader if NOT silent
      if (!isSilent) setDataLoading(true);

      try {
        const payload = { time_zone: getSystemTimezone() };
        const response = await fetchStepsData(payload);
        setEmail(response.email || "");

        if (response?.weekly_summaries?.length > 0) {
          setLastSyncTime(response.last_sync);
          setHasData(true);
          hasFetchedAfterConnect.current = true; // Mark as fetched
          retryCountRef.current = 0; // Reset retry count on success
          return response;
        } else {
          setHasData(false);
          // Still mark as fetched even if no data
          hasFetchedAfterConnect.current = true;
          retryCountRef.current = 0;
          return null;
        }
      } catch (error) {
        console.error("Error in fetching data", error);

        // Increment retry count
        retryCountRef.current += 1;

        // Check if we should mark as fetched to prevent infinite retries
        if (retryCountRef.current >= 3) {
          console.log("Max retry attempts reached, marking as fetched");
          hasFetchedAfterConnect.current = true;
        }

        toast.error(
          error.response?.data?.message ||
            "Failed to fetch data. Please try again."
        );
        setHasData(false);
        return null;
      } finally {
        setDataLoading(false);
      }
    },
    [dataLoading]
  );

  const handleSyncNow = useCallback(async () => {
    return await handleFetchData(true);
  }, [handleFetchData]);

  const handleSuccess = useCallback(async (codeResponse) => {
    setOpen(false);
    try {
      setConnectingGoogleFit(true);
      await connectWithGoogle(codeResponse?.code);
      setGoogleFitConnect(true);
      localStorage.setItem("is_google_fit_connect", "true");

      // Reset the fetch flag when connecting
      hasFetchedAfterConnect.current = false;
      retryCountRef.current = 0; // Reset retry count on new connection
      setConnectingGoogleFit(false);

      // Show success message
      toast.info("Successfully connected to Google Fit!");
    } catch (error) {
      console.error("❌ Error sending code to backend:", error);
      toast.error(
        error.response?.data?.message ||
          "Google login failed. Please try again."
      );
      setConnectingGoogleFit(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    setLogoutLoading(true);
    setGoogleFitConnect(false);
    localStorage.setItem("is_google_fit_connect", "false");

    // Reset fetch flag on logout
    hasFetchedAfterConnect.current = false;
    retryCountRef.current = 0;

    try {
      await googleLogout();
      toast.info("Successfully logged out from Google Fit.");
    } catch (error) {
      console.log("Error in logging out");
      toast.error("Error in logging out. Please try again.");
    } finally {
      setLogoutLoading(false);
    }
  }, []);

  return {
    googleFitConnect,
    setGoogleFitConnect,
    email,
    setEmail,
    open,
    setOpen,
    connectingGoogleFit,
    setConnectingGoogleFit,
    dataLoading,
    setDataLoading,
    hasData,
    setHasData,
    lastSyncTime,
    setLastSyncTime,
    logoutLoading,
    setLogoutLoading,
    currentWeekIndex,
    setCurrentWeekIndex,
    weeklyData,
    setWeeklyData,
    overallData,
    setOverallData,
    calendarAnchor,
    setCalendarAnchor,
    calendarDate,
    setCalendarDate,
    currentWeekRange,
    setCurrentWeekRange,
    chartData,
    setChartData,
    goals,
    setGoals,
    summaryStats,
    setSummaryStats,
    handleFetchData,
    handleSyncNow,
    handleSuccess,
    handleLogout,
    getSystemTimezone,
    datesAvailable,
    setDatesAvailable,
    hasFetchedAfterConnect: hasFetchedAfterConnect.current,
  };
};
